import { useContext, useEffect, useMemo, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { AuthContext } from '../../contexts/auth'
import { sdk } from '../../services/sdk'
import { Button } from '../Button'
import { FeedbackAlertContext } from '../FeedbackAlert'
import { FormInputPassword } from '../formComponents/FormInputPassword'
import { FormInputText } from '../formComponents/FormInputText'
import { LoadingScreen } from '../LoadingScreen'

import styles from './styles.module.scss'

interface ChangePasswordProps {
  token?: string
  title?: string
}

export const ChangePasswordForm = ({ title, token }: ChangePasswordProps) => {
  const { error, user } = useContext(AuthContext)
  const { feedbackAlert } = useContext(FeedbackAlertContext)
  const navigate = useNavigate()

  const [validPassword, setValidPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const defaultValues = {
    email: user?.user.email ? user.user.email : '',
    newPassword: '',
    confirmNewPassword: '',
  }

  const methods = useForm({
    defaultValues,
  })

  const { handleSubmit, control, watch } = useMemo(() => methods, [methods])
  const { email, newPassword, confirmNewPassword } = watch()

  useEffect(() => {
    if (newPassword.length && confirmNewPassword.length) {
      setValidPassword(newPassword === confirmNewPassword)
    } else {
      setValidPassword(true)
    }
  }, [newPassword, confirmNewPassword, email])

  const submit = async () => {
    if (validPassword && newPassword.length && confirmNewPassword.length) {
      try {
        setLoading(true)
        const response = user
          ? await sdk.user.updateUser({
              password: newPassword,
            })
          : token &&
            (await sdk.user.updateRecoveryPassword(token, email, newPassword))

        if (response && !user) {
          navigate('/login')
        } else {
          feedbackAlert('Senha alterada com sucesso!', 'success')
        }
      } catch (error) {
        feedbackAlert(
          'Algo deu errado, verifique seus dados e tente novamente ou entre em contato via e-mail com fairfashion@blockforce.in',
          'error',
        )
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <p>
          <strong>{title}</strong>
        </p>
        <small>
          Para mudar sua senha preencha os campos abaixo e depois confirme.
        </small>
      </div>
      <div className={styles.form}>
        {!user && (
          <>
            <FormInputText
              name="email"
              type="email"
              control={control}
              label="Email"
              validate={{
                required: 'Campo obrigatório',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Email inválido',
                },
              }}
            />
            {error && (
              <small className={styles.error}>
                *Não encontramos esse email em nossa base de dados.
              </small>
            )}
          </>
        )}
        <FormInputPassword
          name="newPassword"
          control={control}
          defaultValue=""
          label="Nova senha"
          validate={{
            required: 'Campo obrigatório',
          }}
        />
        <FormInputPassword
          name="confirmNewPassword"
          control={control}
          defaultValue=""
          label="Confirme a nova senha"
          validate={{
            required: 'Campo obrigatório',
          }}
        />
        <div className={styles.invalidPassword}>
          <small>{!validPassword && '- As senhas não coincidem -'}</small>
        </div>
      </div>

      <div className={styles.button}>
        <Button
          fullWidth
          title="Salvar"
          type="submit"
          onClick={handleSubmit(submit)}
          disabled={
            validPassword &&
            email &&
            newPassword.length &&
            confirmNewPassword.length &&
            !loading
              ? false
              : true
          }
        />
      </div>
      {loading && <LoadingScreen backgroundColor="transparent" />}
    </div>
  )
}
